import React, { useContext, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import DataTable from "react-data-table-component";
import { gql, useQuery, useMutation } from "@apollo/client";
import StarekContext from "../../context/StarekContext";
import EntityLoadingState from "../../components/EntityLoadingState";
import EntityEmptyState from "../../components/EntityEmptyState";
import { EMPTY_MESSAGES } from "../../constants/emptyStateMessages";
import RoleSidebar from "./RoleSidebar";
import EntityOperationBar from "../../components/EntityOperationBar";
import ConfirmModal from "../../components/modals/ConfirmModal";
import { usePermissions } from "../../hooks/usePermissions";
import { buildListRoute, parseRouteEntityId } from "../../utils";
import usePagedList, { sortToOrderBy } from "../../hooks/usePagedList";
import Paginator from "../../components/Paginator";
import { deactivateData, restoreData } from "../../utils/softDelete";
import "../../starek.css";

// UI Strings
const UI_STRINGS = {
    PAGE_TITLE: "Roles",
    SEARCH_PLACEHOLDER: "Search roles…",
    // Delete confirmation
    DELETE_TITLE: "Delete Role(s)?",
    DELETE_CONFIRM: "Delete",
    DELETE_CANCEL: "Cancel",
    // Success messages
    DELETE_SUCCESS: "Role(s) deleted successfully",
    // Error messages
    DELETE_ERROR: "Failed to delete role(s)",
    NOT_FOUND: "Role not found",
};

// Confirmation messages
const ROLE_MESSAGES = {
    DELETE_ROLE_SINGLE: (name) => `You are about to delete role "${name}".

This action cannot be undone.

Are you sure you wish to continue?`,

    DELETE_ROLES_BULK: (count) => `You are about to delete ${count} role(s).

This action cannot be undone.

Are you sure you wish to continue?`,
};

// Everything the table and the sidebar read off a role.
const ROLE_FIELDS = `
            id
            name
            description
            is_active
            deleted_at
            memberships {
                role_id
            }
            privileges {
                id
                entity_type_id
                permission
            }
`;

// One page of roles plus the total, for server-side pagination (usePagedList).
const GET_ROLES = gql`
    query Roles(
        $where: RoleWhereInput
        $orderBy: [RoleOrderByWithRelationInput!]
        $take: Int
        $skip: Int
    ) {
        roles(where: $where, orderBy: $orderBy, take: $take, skip: $skip) {
            ${ROLE_FIELDS}
        }
        aggregateRole(where: $where) {
            _count {
                _all
            }
        }
    }
`;

// A deep link (/roles/:id) may point at a role that is not on the current page.
// findFirst goes through the soft-delete filter, so an archived role is not found.
const GET_ROLE_BY_ID = gql`
    query GetRoleById($where: RoleWhereInput) {
        findFirstRole(where: $where) {
            ${ROLE_FIELDS}
        }
    }
`;

const UPDATE_MANY_ROLE = gql`
    mutation UpdateManyRole($data: RoleUpdateManyMutationInput!, $where: RoleWhereInput) {
        updateManyRole(data: $data, where: $where) {
            count
        }
    }
`;

const DELETE_ONE_ROLE = gql`
    mutation DeleteOneRole($id: Int!) {
        deleteOneRole(where: { id: $id }) {
            id
        }
    }
`;

const DELETE_MANY_ROLE = gql`
    mutation DeleteManyRole($ids: [Int!]!) {
        deleteManyRole(where: { id: { in: $ids } }) {
            count
        }
    }
`;

const Roles = () => {
    const starekContext = useContext(StarekContext);
    const navigate = useNavigate();
    const { id: routeId } = useParams();
    const routeRoleId = parseRouteEntityId(routeId);
    const { can } = usePermissions();
    const canWrite = can("role", "WRITE");
    const canDestroy = can("role", "DESTROY");

    const [searchParams, setSearchParams] = useSearchParams();

    // View state lives in the URL so a refresh, a deep link or a back-navigation
    // lands on exactly the same list the user was looking at.
    const showInactive = searchParams.get("showInactive") === "1";

    const setShowInactive = (value, options = {}) => {
        const next = new URLSearchParams(searchParams);
        if (value) {
            next.set("showInactive", "1");
        } else {
            next.delete("showInactive");
        }
        setSearchParams(next, options);
    };

    const [filterText, setFilterText] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [toggleCleared, setToggleCleared] = useState(false);
    const [showWizard, setShowWizard] = useState(false);
    const [routeEditRole, setRouteEditRole] = useState(null);
    const [confirmCfg, setConfirmCfg] = useState({ open: false });
    // Ids just deleted: the refresh drops them while the route may still point at one.
    const deletedIdsRef = useRef(new Set());

    const roleQueryWhere = useMemo(
        () => (showInactive ? {} : { is_active: { equals: true } }),
        [showInactive],
    );

    // Table sort, applied on the server: { field, direction } or null for default order.
    const [sort, setSort] = useState(null);
    const orderBy = useMemo(() => sortToOrderBy(sort, ["description"]), [sort]);

    // One page (12) of roles from the server, network-only on every run.
    const {
        items,
        total,
        page,
        pageCount,
        pageSize,
        setPage,
        loading,
        initialLoading,
        error,
        refetch,
    } = usePagedList({
        query: GET_ROLES,
        listField: "roles",
        countField: "aggregateRole",
        where: roleQueryWhere,
        searchText: filterText,
        orderBy,
        client: starekContext.engineClient,
    });

    // Selection is per page: moving pages clears it.
    useEffect(() => {
        setSelectedRows([]);
        setToggleCleared((p) => !p);
    }, [page]);

    // /roles/:id may point at a role that is not on this page, so it is loaded on its
    // own (whatever its is_active; archived roles are excluded server-side).
    const {
        data: routeRoleData,
        loading: routeRoleLoading,
        refetch: refetchRouteRole,
    } = useQuery(GET_ROLE_BY_ID, {
        client: starekContext.engineClient,
        skip: routeRoleId == null,
        variables: { where: { id: { equals: routeRoleId } } },
        fetchPolicy: "network-only",
    });

    /**
     * Re-reads the page after a write, and the open role too when it is not on the
     * page, so the sidebar sees the write. A just-deleted role is skipped — it would
     * read as "not found". A failed refetch must not take the write down with it.
     */
    const hardRefresh = async () => {
        try {
            const res = await refetch();
            const onPage = (res?.data?.roles ?? []).some(
                (r) => Number(r.id) === routeRoleId,
            );
            if (
                routeRoleId != null &&
                !deletedIdsRef.current.has(routeRoleId) &&
                !onPage
            ) {
                await refetchRouteRole();
            }
        } catch (err) {
            console.error("Failed to refresh roles:", err);
        }
    };

    const [deleteOneRole, { loading: deletingOne }] = useMutation(
        DELETE_ONE_ROLE,
        {
            client: starekContext.engineClient,
        },
    );
    const [deleteManyRole, { loading: deletingMany }] = useMutation(
        DELETE_MANY_ROLE,
        {
            client: starekContext.engineClient,
        },
    );
    const [updateManyRole] = useMutation(UPDATE_MANY_ROLE, { client: starekContext.engineClient });

    const isDeleting = deletingOne || deletingMany;

    useEffect(() => {
        if (error) starekContext.showError(error);
    }, [error, starekContext]);

    useEffect(() => {
        if (!routeId) {
            setRouteEditRole(null);
            return;
        }

        if (routeRoleLoading) return;

        const listRoute = buildListRoute("/roles", searchParams);

        if (routeRoleId == null) {
            starekContext.showError(UI_STRINGS.NOT_FOUND);
            setRouteEditRole(null);
            navigate(listRoute, { replace: true });
            return;
        }

        // Just deleted from the sidebar: the sidebar closes itself. Checked before the
        // lookup, because the by-id result can still hold the deleted row for a moment.
        if (deletedIdsRef.current.has(routeRoleId)) return;

        // Prefer the row on the current page (fresh after every refresh); otherwise the
        // role loaded by id. Either way inactive roles open without widening the list.
        const roleToEdit =
            items.find((role) => Number(role?.id) === routeRoleId) ||
            routeRoleData?.findFirstRole;

        if (!roleToEdit) {
            starekContext.showError(UI_STRINGS.NOT_FOUND);
            setRouteEditRole(null);
            navigate(listRoute, { replace: true });
            return;
        }

        setRouteEditRole(roleToEdit);
        setSelectedRows((current) =>
            current.length === 1 && Number(current[0]?.id) === routeRoleId
                ? current
                : [roleToEdit],
        );
        setShowWizard(true);
    }, [
        routeId,
        routeRoleId,
        routeRoleLoading,
        routeRoleData,
        items,
        searchParams,
        navigate,
        starekContext,
    ]);

    // Keep the list's URL state (?page, ?showInactive) across opening and closing a role.
    const openRole = (id) => navigate(buildListRoute(`/roles/${id}`, searchParams));

    const handleAdd = () => {
        setRouteEditRole(null);
        setSelectedRows([]);
        setToggleCleared((p) => !p);
        setShowWizard(true);
    };

    const handleEdit = () => {
        if (selectedRows.length === 1) {
            openRole(selectedRows[0].id);
        }
    };

    const handleDelete = () => {
        if (selectedRows.length === 0) return;

        const count = selectedRows.length;
        const roleName = selectedRows[0]?.name || "Unknown";

        setConfirmCfg({
            open: true,
            type: "danger",
            title: UI_STRINGS.DELETE_TITLE,
            content:
                count === 1
                    ? ROLE_MESSAGES.DELETE_ROLE_SINGLE(roleName)
                    : ROLE_MESSAGES.DELETE_ROLES_BULK(count),
            confirmText: UI_STRINGS.DELETE_CONFIRM,
            cancelText: UI_STRINGS.DELETE_CANCEL,
            onConfirm: async () => {
                const ids = selectedRows.map((r) => Number(r.id));

                try {
                    if (ids.length === 1) {
                        await deleteOneRole({ variables: { id: ids[0] } });
                    } else {
                        await deleteManyRole({ variables: { ids } });
                    }
                    ids.forEach((id) => deletedIdsRef.current.add(id));
                    starekContext?.showSuccess?.(UI_STRINGS.DELETE_SUCCESS);
                } catch (error) {
                    starekContext.showError(
                        error?.message || UI_STRINGS.DELETE_ERROR,
                    );
                } finally {
                    setSelectedRows([]);
                    setToggleCleared((p) => !p);
                    await hardRefresh();
                    setConfirmCfg({ open: false });
                }
            },
            onClose: () => setConfirmCfg({ open: false }),
        });
    };

    const handleDrawerClose = async () => {
        setShowWizard(false);
        setRouteEditRole(null);
        setSelectedRows([]);
        setToggleCleared((p) => !p);
        if (routeId) {
            navigate(buildListRoute("/roles", searchParams), { replace: true });
        }
        await hardRefresh();
    };

    const handleSaveRole = async () => {
        await handleDrawerClose();
    };

    const handleDeleteRole = async (role) => {
        if (!role?.id) return;

        const roleName = role.name || "Unknown";
        setConfirmCfg({
            open: true,
            type: "danger",
            title: UI_STRINGS.DELETE_TITLE,
            content: ROLE_MESSAGES.DELETE_ROLE_SINGLE(roleName),
            confirmText: UI_STRINGS.DELETE_CONFIRM,
            cancelText: UI_STRINGS.DELETE_CANCEL,
            onConfirm: async () => {
                try {
                    await deleteOneRole({ variables: { id: Number(role.id) } });
                    deletedIdsRef.current.add(Number(role.id));
                    starekContext?.showSuccess?.(UI_STRINGS.DELETE_SUCCESS);
                } catch (error) {
                    starekContext.showError(
                        error?.message || UI_STRINGS.DELETE_ERROR,
                    );
                } finally {
                    await handleDrawerClose();
                    setConfirmCfg({ open: false });
                }
            },
            onClose: () => setConfirmCfg({ open: false }),
        });
    };

    const activeRole = routeEditRole || selectedRows[0] || null;

    const columns = [
        {
            name: "Name",
            selector: (r) => r.name || "-",
            sortable: true,
            sortField: "name",
            grow: 1.2,
            cell: (row) => (
                <span
                    role="button"
                    className="text-primary"
                    onClick={() => openRole(row.id)}
                >
                    {row.name || "-"}
                </span>
            ),
        },
        {
            name: "Description",
            selector: (r) => r.description || "-",
            sortable: false,
            grow: 2,
            cell: (row) => (
                <div className="text-truncate" style={{ maxWidth: 520 }}>
                    {row.description || "-"}
                </div>
            ),
        },
    ];

    const customStyles = {
        headCells: { style: { fontWeight: "bold" } },
        rows: { style: { minHeight: "60px" } },
        cells: {
            style: {
                paddingTop: "12px",
                paddingBottom: "12px",
                whiteSpace: "normal",
                lineHeight: "1.2rem",
            },
        },
    };

    const handleToggleActive = async (roles, makeActive) => {
        const userId = starekContext?.currentUser?.id ?? starekContext?.user?.id;
        const ids = roles.map((r) => r.id);
        try {
            await updateManyRole({
                variables: {
                    data: makeActive ? restoreData() : deactivateData(),
                    where: { id: { in: ids } },
                },
            });
            starekContext.showSuccess(makeActive ? `Restored ${ids.length} role(s)` : `Deactivated ${ids.length} role(s)`);
            setSelectedRows([]);
            setToggleCleared((p) => !p);
            await hardRefresh();
        } catch (err) {
            starekContext.showError(err);
        }
    };

    const roleExtendedActions = selectedRows.length > 0 ? [
        {
            label: "Deactivate",
            icon: "fa-ban",
            danger: true,
            disabled: selectedRows.every((r) => !r.is_active),
            onClick: (rows) => handleToggleActive(rows.filter((r) => r.is_active), false),
        },
        {
            label: "Restore",
            icon: "fa-undo",
            disabled: selectedRows.every((r) => r.is_active),
            onClick: (rows) => handleToggleActive(rows.filter((r) => !r.is_active), true),
        },
    ] : [];

    const roleConditionalRowStyles = [
        { when: (r) => !r.is_active, style: { opacity: 0.5, backgroundColor: "#f8f9fa" } },
    ];

    // Only the first load gets the full-page spinner; later loads (Show Inactive, a
    // page change, a refetch after a write) keep the list and any open sidebar on screen.
    if (initialLoading) return <EntityLoadingState icon="fa-lock" />;

    return (
        <div className="container-fluid">
            <div className="font-weight-bold mb-3">{UI_STRINGS.PAGE_TITLE}</div>

            <EntityOperationBar
                entity={{ singular: "Role", plural: "Roles" }}
                allowSearch={true}
                allowAdd={canWrite}
                allowDelete={canDestroy}
                allowEdit={canWrite}
                allowExtendedActions={canWrite && selectedRows.length > 0}
                extendedActions={roleExtendedActions}
                allowShowInactive={true}
                showInactive={showInactive}
                onShowInactiveChange={(v) => { setShowInactive(v); setSelectedRows([]); setToggleCleared((p) => !p); }}
                selectedRows={selectedRows}
                isDeleting={isDeleting}
                searchPlaceholder={UI_STRINGS.SEARCH_PLACEHOLDER}
                onSearch={(text) => setFilterText(text)}
                onAdd={handleAdd}
                onViewChange={() => { }}
                onDelete={handleDelete}
                onEdit={handleEdit}
            >
                <EntityOperationBar.View
                    mode="list"
                    icon="fa-list"
                    tooltip="List View"
                >
                    {/* `items` is the current page, searched and sorted on the server; the
                        Paginator below handles paging, so the table's own pager is off. */}
                    <DataTable
                        columns={columns}
                        data={items}
                        selectableRows
                        onSelectedRowsChange={({ selectedRows }) =>
                            setSelectedRows(selectedRows)
                        }
                        clearSelectedRows={toggleCleared}
                        highlightOnHover
                        pointerOnHover
                        onRowClicked={(row) => openRole(row.id)}
                        responsive
                        dense
                        sortServer
                        onSort={(column, direction) =>
                            setSort(column.sortField ? { field: column.sortField, direction } : null)
                        }
                        selectableRowsHighlight
                        selectableRowsNoSelectAll={false}
                        customStyles={customStyles}
                        noDataComponent={<EntityEmptyState icon="fa-lock" entityName="Role" messages={EMPTY_MESSAGES.roles} isFiltered={!!filterText.trim()} onAdd={handleAdd} />}
                        conditionalRowStyles={roleConditionalRowStyles}
                    />
                </EntityOperationBar.View>
            </EntityOperationBar>

            <Paginator
                page={page}
                pageCount={pageCount}
                pageSize={pageSize}
                total={total}
                onChange={setPage}
                disabled={loading}
            />

            <RoleSidebar
                show={showWizard}
                item={activeRole}
                onDiscard={handleDrawerClose}
                onSave={canWrite ? handleSaveRole : null}
                onDelete={canDestroy ? handleDeleteRole : null}
                onToggleActive={canWrite ? (entity) => handleToggleActive([entity], !entity.is_active) : undefined}
            />

            <ConfirmModal
                open={confirmCfg.open}
                type={confirmCfg.type || "danger"}
                title={confirmCfg.title}
                content={confirmCfg.content}
                confirmText={confirmCfg.confirmText}
                cancelText={confirmCfg.cancelText}
                loading={isDeleting}
                onConfirm={confirmCfg.onConfirm}
                onClose={confirmCfg.onClose}
            />
        </div>
    );
};

export { Roles };
